import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const MOSAIC_SIZE = 5;
/** The palette every picture is drawn from. */
export const MOSAIC_TILES = ["🟥", "🟦", "🟨", "🟩", "⬜"] as const;

/** Simple, recognisable pictures on a 5 × 5 grid, drawn with palette indexes. */
const PICTURES: readonly { name: string; rows: string[] }[] = [
	{
		name: "a heart",
		rows: ["40004", "00000", "00000", "40004", "44044"].map((row) => row),
	},
	{ name: "a tree", rows: ["44344", "43334", "33333", "44144", "44144"] },
	{ name: "a house", rows: ["44044", "40004", "00000", "01102", "01102"] },
	{ name: "a smiley", rows: ["42224", "21212", "22222", "21112", "42224"] },
	{ name: "a sailboat", rows: ["44144", "44114", "41111", "44444", "11111"] },
	{ name: "a flower", rows: ["40404", "44044", "43334", "44344", "42224"] },
];

export interface MosaicState extends BaseState {
	kind: "mosaic";
	/** The picture to copy, as palette indexes. */
	target: number[];
	name: string;
	/** What has been placed so far — `-1` for an empty square. */
	board: number[];
	/** Who placed each tile, for colouring the score. */
	owner: (PlayerIndex | null)[];
	lastPlaced: number | null;
	lastCorrect: boolean | null;
}

export interface MosaicMove {
	square: number;
	tile: number;
}

export const emojiMosaic: GameDefinition<MosaicState, MosaicMove> = {
	id: "mosaic",
	category: "art",
	title: "Emoji Mosaic",
	emoji: "🎨",
	blurb: "Copy the picture tile by tile. Match a square and score.",
	howTo: [
		"A picture is shown at the top — copy it onto the empty grid.",
		"On your turn pick a colour, then tap a square.",
		"Put the right colour in the right square and you score a point.",
		"Most points when the grid is full wins — a tie goes to whoever went second.",
	],

	create(rng) {
		const picture = PICTURES[rng.int(PICTURES.length)];
		const target = picture.rows.join("").split("").map(Number);
		return {
			kind: "mosaic",
			target,
			name: picture.name,
			board: Array<number>(MOSAIC_SIZE * MOSAIC_SIZE).fill(-1),
			owner: Array<PlayerIndex | null>(MOSAIC_SIZE * MOSAIC_SIZE).fill(null),
			lastPlaced: null,
			lastCorrect: null,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { square, tile } = raw as { square?: unknown; tile?: unknown };
		if (typeof square !== "number" || !Number.isInteger(square)) return null;
		if (square < 0 || square >= MOSAIC_SIZE * MOSAIC_SIZE) return null;
		if (typeof tile !== "number" || !Number.isInteger(tile)) return null;
		if (tile < 0 || tile >= MOSAIC_TILES.length) return null;
		return { square, tile };
	},

	apply(state, player, move): MoveOutcome<MosaicState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.board[move.square] !== -1) return reject("That square is already filled.");

		const board = [...state.board];
		const owner = [...state.owner];
		board[move.square] = move.tile;
		owner[move.square] = player;

		const correct = state.target[move.square] === move.tile;
		const scores: [number, number] = [...state.scores];
		if (correct) scores[player] += 1;

		const finished = board.every((tile) => tile !== -1);
		// Twenty-five squares split 13–12, so whoever opened got the extra go.
		// The grid always fills on the opener's move — turns never skip — so a
		// tied score hands the win to the *other* player, whose twelve careful
		// tiles matched the opener's thirteen.
		let result: ReturnType<typeof resultFromScores> | null = null;
		if (finished) {
			result = resultFromScores(scores);
			if (result.winner === null) result = { winner: otherPlayer(player) };
		}
		return {
			ok: true,
			state: {
				...state,
				board,
				owner,
				scores,
				lastPlaced: move.square,
				lastCorrect: correct,
				turn: finished ? null : otherPlayer(player),
				result,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: MosaicMove[] = [];
		state.board.forEach((tile, square) => {
			if (tile !== -1) return;
			// Only the right colour is worth playing, but any is allowed.
			for (let choice = 0; choice < MOSAIC_TILES.length; choice++) {
				moves.push({ square, tile: choice });
			}
		});
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
